// src/pages/Customers/CustomerDetail.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, collection, updateDoc, deleteDoc, query, where, orderBy, onSnapshot, addDoc, writeBatch, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/formatters';
import useCustomerPricing from '../../hooks/useCustomerPricing';
import * as XLSX from 'xlsx'; // Add this import for Excel export
import { generateReconciliationPDF } from '../../utils/pdfGenerator';
import InvoiceDetailModal from '../Invoices/InvoiceDetailModal';
import toast from 'react-hot-toast';


// Add custom scrollbar styles
const scrollbarCSS = `
  .scrollable-container::-webkit-scrollbar {
    width: 8px;
  }
  
  .scrollable-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  .scrollable-container::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  .scrollable-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

// Add return modal styles
const returnModalStyles = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    },
    modalContent: {
        background: 'white',
        padding: '30px',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #dee2e6',
        paddingBottom: '15px',
        marginBottom: '20px'
    },
    formGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '500',
        color: '#495057'
    },
    input: {
        width: '100%',
        padding: '12px 15px',
        fontSize: '16px',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        boxSizing: 'border-box'
    },
    select: {
        width: '100%',
        padding: '12px 15px',
        fontSize: '16px',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        background: 'white'
    },
    textarea: {
        width: '100%',
        padding: '12px 15px',
        fontSize: '16px',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        boxSizing: 'border-box',
        minHeight: '80px'
    },
    button: {
        padding: '12px 25px',
        border: 'none',
        background: '#198754',
        color: 'white',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '500'
    },
    cancelButton: {
        padding: '12px 25px',
        border: 'none',
        background: '#6c757d',
        color: 'white',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '500',
        marginRight: '10px'
    },
    suggestions: {
        position: 'absolute',
        background: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        width: '100%',
        maxHeight: '150px',
        overflowY: 'auto',
        zIndex: 100,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    },
    suggestionItem: {
        padding: '12px 15px',
        cursor: 'pointer',
        ':hover': {
            background: '#f8f9fa'
        }
    },
    productRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '10px',
        borderBottom: '1px solid #eee'
    },
    productImage: {
        width: '40px',
        height: '40px',
        marginRight: '10px',
        borderRadius: '4px'
    },
    productName: {
        flex: 1
    },
    productQuantity: {
        fontWeight: 'bold',
        color: '#06C167'
    },
    invitationButton: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(39, 174, 96, 0.3)',
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    }
};

function ReturnModal({ onClose, onSave, customer, products, orders }) {
    const [returnItems, setReturnItems] = useState([]);
    const [isManual, setIsManual] = useState(false);
    const [currentItem, setCurrentItem] = useState({
        productId: '',
        productName: '',
        quantity: 1,
        reason: 'hatali_urun',
        description: '',
        unitPrice: 0
    });

    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showProductSuggestions, setShowProductSuggestions] = useState(false);
    const [returnNote, setReturnNote] = useState('');
    const [selectedCurrency, setSelectedCurrency] = useState(customer?.currency || 'TRY');

    const currencySymbol = selectedCurrency === 'USD' ? '$' : '₺';

    const reasonOptions = [
        { value: 'hatali_urun', label: 'Hatalı Ürün' },
        { value: 'kargo_hasari', label: 'Kargo Hasarı' },
        { value: 'degistirme', label: 'Değiştirme' },
        { value: 'istenmeyen_urun', label: 'İstenmeyen Ürün' },
        { value: 'diger', label: 'Diğer' }
    ];

    const getReasonLabel = (value) => reasonOptions.find(r => r.value === value)?.label || value;

    const getCustomerProducts = useCallback(() => {
        const customerProducts = {};
        const productMap = {};
        products.forEach(product => { productMap[product.id] = product; });

        orders.forEach(order => {
            if (order.lineItems) {
                order.lineItems.forEach(item => {
                    const productData = productMap[item.productId];
                    if (productData) {
                        if (!customerProducts[item.productId]) {
                            customerProducts[item.productId] = {
                                productId: item.productId,
                                productName: productData.name || item.productName || 'Bilinmeyen Ürün',
                                totalQuantity: item.quantity,
                                imageUrl: productData.imageUrl || null,
                                unitPrice: productData.salePrice || 0,
                                orderDates: [order.orderDate]
                            };
                        } else {
                            customerProducts[item.productId].totalQuantity += item.quantity;
                            customerProducts[item.productId].orderDates.push(order.orderDate);
                        }
                    }
                });
            }
        });
        return Object.values(customerProducts);
    }, [orders, products]);

    const customerProducts = getCustomerProducts();

    const handleProductSearch = (searchTerm) => {
        if (isManual) return;
        if (searchTerm && searchTerm.length > 0) {
            const filtered = products.filter(product =>
                (product.name && product.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (product.barcode && product.barcode.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            setFilteredProducts(filtered);
            setShowProductSuggestions(true);
        } else {
            setFilteredProducts([]);
            setShowProductSuggestions(false);
        }
    };

    const handleProductSelect = (product) => {
        setCurrentItem(prev => ({
            ...prev,
            productId: product.id,
            productName: product.name,
            unitPrice: product.salePrice || 0
        }));
        setShowProductSuggestions(false);
    };

    const handleCurrentItemChange = (e) => {
        const { name, value } = e.target;
        setCurrentItem(prev => ({ ...prev, [name]: value }));
        if (name === 'productName' && !isManual) {
            handleProductSearch(value);
        }
    };

    const handleAddItem = () => {
        if (!currentItem.productName || !currentItem.quantity || currentItem.quantity <= 0) {
            alert('Lütfen geçerli bir ürün adı ve miktar seçin.');
            return;
        }

        if (!isManual) {
            if (!currentItem.productId) {
                alert('Lütfen listeden bir ürün seçin veya Manuel Giriş moduna geçin.');
                return;
            }

            const orderedProduct = customerProducts.find(p => p.productId === currentItem.productId);
            const existingItems = returnItems.filter(item => item.productId === currentItem.productId);
            const totalExistingQuantity = existingItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
            const newTotalQuantity = totalExistingQuantity + Number(currentItem.quantity || 0);

            if (orderedProduct && newTotalQuantity > orderedProduct.totalQuantity) {
                alert(`Bu üründen müşteriye sadece ${orderedProduct.totalQuantity} adet gönderilmiştir. Daha fazla iade edemezsiniz.`);
                return;
            }
        }

        setReturnItems(prev => [...prev, {
            ...currentItem,
            isManual: isManual,
            quantity: Number(currentItem.quantity),
            unitPrice: Number(currentItem.unitPrice || 0),
            id: Date.now(),
            totalPrice: Number(currentItem.unitPrice || 0) * Number(currentItem.quantity || 0)
        }]);

        setCurrentItem({
            productId: '',
            productName: '',
            quantity: 1,
            reason: 'hatali_urun',
            description: '',
            unitPrice: 0
        });
        setIsManual(false);
    };

    const handleRemoveItem = (id) => {
        setReturnItems(prev => prev.filter(item => item.id !== id));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const totalAmount = returnItems.reduce((sum, item) => sum + item.totalPrice, 0);

        const returnData = {
            customerId: customer.id,
            customerName: customer.name,
            items: returnItems,
            totalAmount: totalAmount,
            currency: selectedCurrency,
            note: returnNote,
            status: 'completed',
            createdAt: serverTimestamp()
        };

        onSave(returnData);
    };

    const modalEnhancedStyles = {
        overlay: {
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        },
        content: {
            background: 'white', padding: '32px', borderRadius: '16px',
            width: '95%', maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', border: '1px solid #e2e8f0'
        },
        header: {
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderBottom: '2px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px'
        },
        sectionTitle: {
            fontSize: '14px', fontWeight: '600', color: '#64748b',
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px'
        },
        addRow: {
            display: 'grid', gridTemplateColumns: 'minmax(200px, 2.5fr) 100px 120px 150px 1.5fr auto',
            gap: '12px', alignItems: 'flex-end', marginBottom: '24px',
            background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0'
        },
        input: {
            width: '100%', padding: '10px 14px', fontSize: '14px',
            border: '1px solid #e2e8f0', borderRadius: '8px', boxSizing: 'border-box',
            outline: 'none', transition: 'border-color 0.2s'
        },
        select: {
            width: '100%', padding: '10px 14px', fontSize: '14px',
            border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white',
            outline: 'none', cursor: 'pointer'
        },
        addBtn: {
            padding: '10px 20px', border: 'none', background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
            color: 'white', borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
            fontWeight: '600', transition: 'all 0.2s', whiteSpace: 'nowrap', height: '41px'
        },
        manualToggle: {
            marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px',
            cursor: 'pointer', fontSize: '13px', color: '#4f46e5', fontWeight: '600'
        },
        itemCard: {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', background: '#f8fafc', borderRadius: '10px',
            border: '1px solid #e2e8f0', marginBottom: '8px'
        },
        totalBar: {
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '16px 20px', background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            borderRadius: '12px', border: '1px solid #bbf7d0', marginTop: '12px'
        },
        actionsBar: {
            display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px',
            paddingTop: '16px', borderTop: '1px solid #f1f5f9'
        },
        cancelBtn: {
            padding: '12px 24px', border: '1px solid #e2e8f0', background: 'white',
            color: '#64748b', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500'
        },
        saveBtn: {
            padding: '12px 28px', border: 'none', background: 'linear-gradient(135deg, #059669, #10b981)',
            color: 'white', borderRadius: '10px', cursor: 'pointer', fontSize: '14px',
            fontWeight: '600', transition: 'all 0.2s'
        },
        reasonBadge: (reason) => {
            const colors = {
                hatali_urun: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
                kargo_hasari: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
                degistirme: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
                istenmeyen_urun: { bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe' },
                diger: { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' }
            };
            const c = colors[reason] || colors.diger;
            return {
                padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                background: c.bg, color: c.color, border: `1px solid ${c.border}`, display: 'inline-block'
            };
        }
    };

    return (
        <div style={modalEnhancedStyles.overlay} onClick={onClose}>
            <div style={modalEnhancedStyles.content} onClick={e => e.stopPropagation()}>
                <div style={modalEnhancedStyles.header}>
                    <div>
                        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: 0 }}>📦 Ürün İadesi</h2>
                        <p style={{ fontSize: '14px', color: '#64748b', margin: '4px 0 0' }}>Müşteri: <strong>{customer.name}</strong></p>
                    </div>
                    <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', width: '36px', height: '36px', borderRadius: '10px', fontSize: '18px', cursor: 'pointer', color: '#64748b' }}>✕</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '12px' }}>
                        <div style={modalEnhancedStyles.sectionTitle}>Ürün Ekle</div>
                        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Para Birimi:</label>
                                <select 
                                    style={{ ...modalEnhancedStyles.select, width: 'auto', padding: '6px 12px', height: '34px' }}
                                    value={selectedCurrency}
                                    onChange={(e) => setSelectedCurrency(e.target.value)}
                                >
                                    <option value="TRY">Türk Lirası (₺)</option>
                                    <option value="USD">Amerikan Doları ($)</option>
                                </select>
                            </div>
                            <div style={modalEnhancedStyles.manualToggle} onClick={() => {
                                setIsManual(!isManual);
                                setCurrentItem(prev => ({ ...prev, productId: '', productName: '', unitPrice: 0 }));
                            }}>
                                {isManual ? '↩ Listedeye Dön' : '✏ Manuel Ürün Gir'}
                            </div>
                        </div>
                    </div>

                    <div style={modalEnhancedStyles.addRow}>
                        <div style={{ position: 'relative' }}>
                            <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>Ürün {isManual && '(Manuel)'}</label>
                            <input
                                type="text" name="productName" placeholder={isManual ? "Ürün adını yazın..." : "Ürün ara..."}
                                style={modalEnhancedStyles.input}
                                value={currentItem.productName}
                                onChange={handleCurrentItemChange}
                            />
                            {!isManual && showProductSuggestions && filteredProducts.length > 0 && (
                                <div style={{ position: 'absolute', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', width: '100%', maxHeight: '180px', overflowY: 'auto', zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: '4px' }}>
                                    {filteredProducts.map(product => {
                                        const orderedInfo = customerProducts.find(cp => cp.productId === product.id);
                                        return (
                                            <div key={product.id}
                                                onMouseDown={() => handleProductSelect(product)}
                                                style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'white'}
                                            >
                                                {product.imageUrl ? (
                                                    <img src={product.imageUrl} alt="" style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                                                ) : (
                                                    <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>📦</div>
                                                )}
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>
                                                        {product.name}
                                                        {product.barcode && <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px', fontWeight: '400' }}>({product.barcode})</span>}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#64748b' }}>Stok: {product.quantity || 0} {product.unit || 'Adet'}</div>
                                                </div>
                                                {orderedInfo && (
                                                    <span style={{ fontSize: '11px', background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}>
                                                        Siparişte Var ({orderedInfo.totalQuantity})
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div>
                            <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>Miktar</label>
                            <input type="number" name="quantity" min="1" style={modalEnhancedStyles.input} value={currentItem.quantity} onChange={handleCurrentItemChange} />
                        </div>

                        <div>
                            <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>Birim Fiyat ({currencySymbol})</label>
                            <input
                                type="number" name="unitPrice" step="0.01"
                                style={modalEnhancedStyles.input}
                                value={currentItem.unitPrice}
                                onChange={handleCurrentItemChange}
                            />
                        </div>

                        <div>
                            <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>İade Nedeni</label>
                            <select name="reason" style={modalEnhancedStyles.select} value={currentItem.reason} onChange={handleCurrentItemChange}>
                                {reasonOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>Açıklama</label>
                            <input type="text" name="description" placeholder="Detay..." style={modalEnhancedStyles.input} value={currentItem.description} onChange={handleCurrentItemChange} />
                        </div>

                        <div>
                            <button type="button" onClick={handleAddItem} style={modalEnhancedStyles.addBtn}>+ Ekle</button>
                        </div>
                    </div>

                    {/* Added Items List */}
                    {returnItems.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <div style={modalEnhancedStyles.sectionTitle}>İade Edilecek Ürünler ({returnItems.length})</div>
                            {returnItems.map(item => (
                                <div key={item.id} style={modalEnhancedStyles.itemCard}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                                            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
                                                {item.productName} {item.isManual && <span style={{ fontSize: '11px', color: '#06C167', verticalAlign: 'middle' }}>(Manuel)</span>}
                                            </span>
                                            <span style={modalEnhancedStyles.reasonBadge(item.reason)}>{getReasonLabel(item.reason)}</span>
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#64748b' }}>
                                            {item.quantity} adet × {(item.unitPrice || 0).toFixed(2)} {currencySymbol} = <strong style={{ color: '#1e293b' }}>{(item.totalPrice || 0).toFixed(2)} {currencySymbol}</strong>
                                            {item.description && <span style={{ marginLeft: '12px', fontStyle: 'italic' }}>({item.description})</span>}
                                        </div>
                                    </div>
                                    <button type="button" onClick={() => handleRemoveItem(item.id)}
                                        style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                                </div>
                            ))}
                            <div style={modalEnhancedStyles.totalBar}>
                                <span style={{ fontWeight: '600', color: '#065f46', fontSize: '15px' }}>Toplam İade Tutarı</span>
                                <span style={{ fontWeight: '700', color: '#059669', fontSize: '18px' }}>
                                    {returnItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0).toFixed(2)} {currencySymbol}
                                </span>
                            </div>
                        </div>
                    )}

                    <div style={{ marginBottom: '16px' }}>
                        <div style={modalEnhancedStyles.sectionTitle}>Genel Not</div>
                        <textarea
                            name="returnNote" placeholder="İade hakkında genel notlar..."
                            style={{ ...modalEnhancedStyles.input, minHeight: '70px', resize: 'vertical' }}
                            value={returnNote} onChange={(e) => setReturnNote(e.target.value)}
                        />
                    </div>

                    <div style={modalEnhancedStyles.actionsBar}>
                        <button type="button" onClick={onClose} style={modalEnhancedStyles.cancelBtn}>İptal</button>
                        <button type="submit" style={{ ...modalEnhancedStyles.saveBtn, opacity: returnItems.length === 0 ? 0.5 : 1 }} disabled={returnItems.length === 0}>
                            ✓ İadeleri Kaydet
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const styles = {
    container: { fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
    backLink: { textDecoration: 'none', color: '#06C167', marginBottom: '15px', display: 'inline-block', fontSize: '16px' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '30px', alignItems: 'start' },
    box: { background: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
    infoList: { listStyle: 'none', padding: 0 },
    infoItem: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0', alignItems: 'center' },
    infoLabel: { color: '#6c757d', flexBasis: '40%' },
    infoValue: { fontWeight: '500', color: '#212529', flexBasis: '60%', textAlign: 'right' },
    balance: { fontSize: '28px', fontWeight: 'bold', textAlign: 'center', padding: '20px 0' },
    balancePositive: { color: '#198754' },
    balanceNegative: { color: '#dc3545' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '20px' },
    th: { background: '#f8f9fa', padding: '12px 15px', textAlign: 'left', borderBottom: '2px solid #e9ecef', color: '#6c757d', fontSize: '14px', fontWeight: '600' },
    td: { padding: '15px', borderBottom: '1px solid #e9ecef', color: '#495057' },
    trHover: { cursor: 'pointer' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { background: 'white', padding: '30px', borderRadius: '8px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #dee2e6', paddingBottom: '15px', marginBottom: '20px' },
    title: { fontSize: '24px', fontWeight: 'bold', color: '#212529' },
    button: { padding: '10px 18px', border: 'none', background: '#06C167', color: 'white', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s' },
    buttonIcon: { padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '16px', transition: 'background 0.2s', borderRadius: '4px', ':hover': { background: '#f8f9fa' } },
    input: { width: '100%', padding: '8px', border: '1px solid #dee2e6', borderRadius: '6px', boxSizing: 'border-box' },
    select: { width: '100%', padding: '8px', border: '1px solid #dee2e6', borderRadius: '6px', background: 'white' },
    tabContainer: { display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '25px', gap: '20px' },
    tab: {
        padding: '12px 0',
        cursor: 'pointer',
        borderBottom: '2px solid transparent',
        color: '#64748b',
        fontWeight: '500',
        background: 'none',
        fontSize: '15px',
        transition: 'all 0.2s'
    },
    tabActive: { borderBottom: '2px solid #06C167', color: '#06C167', fontWeight: '600' },
    tabContent: { padding: '20px 0' },
    tabTitle: { fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#6c757d' },
    emptyIcon: { fontSize: '48px', marginBottom: '10px' },
    emptyText: { fontSize: '16px' },
    tableContainer: { overflowX: 'auto' },
    statusBadge: { padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' },
    // Added scrollable container styles
    scrollableContainer: {
        maxHeight: '500px',
        overflowY: 'auto',
        paddingRight: '10px',
        scrollbarWidth: 'thin',
        scrollbarColor: '#c1c1c1 #f1f1f1'
    }
};

function SalesInvoiceDetailModal({ invoice, products, customer, onClose, canViewPrices }) {
    const getProductName = useCallback((productId) => {
        const product = products.find(p => p.id === productId);
        return product ? product.name : 'Bilinmeyen Ürün';
    }, [products]);

    const currencySymbol = invoice.currency === 'USD' ? '$' : '₺';

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Satış Faturası Detayı</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                </div>
                <ul style={styles.infoList}>
                    <li style={styles.infoItem}><span style={styles.infoLabel}>Müşteri</span><span style={styles.infoValue}>{invoice.customerName}</span></li>
                    <li style={styles.infoItem}><span style={styles.infoLabel}>Tarih</span><span style={styles.infoValue}>{new Date(invoice.invoiceDate.seconds * 1000).toLocaleDateString()}</span></li>
                    <li style={styles.infoItem}>
                        <span style={styles.infoLabel}>Toplam Tutar</span>
                        <span style={styles.infoValue}>
                            {canViewPrices ? `${invoice.total.toFixed(2)} ${currencySymbol}` : '***'}
                            {customer && invoice.currency !== customer.currency && (
                                <span style={{ fontSize: '12px', color: '#6c757d', marginLeft: '5px' }}>
                                    (Müşteri: {customer.currency === 'USD' ? '$' : '₺'})
                                </span>
                            )}
                        </span>
                    </li>
                </ul>
                <table style={styles.table}>
                    <thead><tr><th style={styles.th}>Ürün Adı</th><th style={styles.th}>Miktar</th><th style={styles.th}>Satış Fiyatı ({currencySymbol})</th><th style={styles.th}>Toplam ({currencySymbol})</th></tr></thead>
                    <tbody>
                        {invoice.lineItems.map((item, index) => (
                            <tr key={index}>
                                <td style={styles.td}>{getProductName(item.productId)}</td>
                                <td style={styles.td}>{item.quantity}</td>
                                <td style={styles.td}>{canViewPrices ? `${item.price.toFixed(2)} ${currencySymbol}` : '***'}</td>
                                <td style={styles.td}>{canViewPrices ? `${(item.price * item.quantity).toFixed(2)} ${currencySymbol}` : '***'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ReconciliationModal({ onClose, onConfirm, lastDate }) {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [useDateRange, setUseDateRange] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        onConfirm(useDateRange ? { start: startDate, end: endDate } : null);
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={{ ...styles.modalContent, maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Mutabakat Oluştur</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Rapor Kapsamı</label>
                        <div style={{ display: 'flex', gap: '15px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                <input type="radio" checked={!useDateRange} onChange={() => setUseDateRange(false)} /> Tüm Dönem
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                <input type="radio" checked={useDateRange} onChange={() => setUseDateRange(true)} /> Belirli Tarih Aralığı
                            </label>
                        </div>
                    </div>

                    {useDateRange && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '5px' }}>Başlangıç</label>
                                <input type="date" style={styles.input} value={startDate} onChange={e => setStartDate(e.target.value)} required />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '5px' }}>Bitiş</label>
                                <input type="date" style={styles.input} value={endDate} onChange={e => setEndDate(e.target.value)} required />
                            </div>
                        </div>
                    )}

                    {lastDate && (
                        <div style={{ marginBottom: '20px', padding: '10px', background: '#f8fafc', borderRadius: '8px', fontSize: '13px', color: '#64748b' }}>
                            ℹ️ Son mutabakat tarihi: {lastDate.seconds ? new Date(lastDate.seconds * 1000).toLocaleDateString('tr-TR') : new Date(lastDate).toLocaleDateString('tr-TR')}
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px' }}>
                        <button type="button" onClick={onClose} style={{ ...styles.button, background: '#6c757d' }}>İptal</button>
                        <button type="submit" style={{ ...styles.button, background: '#06C167' }}>PDF Oluştur</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function CustomerDetail({ customerId: propCustomerId, onBack: propOnBack }) {
    const { customerId: paramsCustomerId } = useParams();
    const customerId = propCustomerId || paramsCustomerId;
    const navigate = useNavigate();
    const { userData, canViewPrices } = useAuth();
    const { customerPrices, customerGroups } = useCustomerPricing(userData?.companyId);
    const [customer, setCustomer] = useState(null);
    const [orders, setOrders] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [invoices, setInvoices] = useState([]); // Add invoices state
    const [balanceHistory, setBalanceHistory] = useState([]); // Add balance history state
    const [products, setProducts] = useState([]); // Add products state
    const [returns, setReturns] = useState([]); // Add returns state
    const [linkedSupplier, setLinkedSupplier] = useState(null); // Add linked supplier for unified balance
    const [companyInfo, setCompanyInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('info');
    const [editing, setEditing] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [invitationLoading, setInvitationLoading] = useState(false);
    const [invitationSent, setInvitationSent] = useState(false);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [showReconciliationModal, setShowReconciliationModal] = useState(false);
    const [orderSubTab, setOrderSubTab] = useState('open');
    const [expandedReturnId, setExpandedReturnId] = useState(null);
    const [returnDateFilter, setReturnDateFilter] = useState('all');

    // Function to export customer data to Excel
    const handleExportCustomerData = () => {
        if (!customer) return;

        // Prepare customer info data
        const customerInfo = [{
            'Alan': 'Müşteri Adı',
            'Değer': customer.name || '-'
        }, {
            'Alan': 'E-posta',
            'Değer': customer.email || '-'
        }, {
            'Alan': 'Telefon',
            'Değer': customer.phone || '-'
        }, {
            'Alan': 'İl',
            'Değer': customer.city || '-'
        }, {
            'Alan': 'İlçe',
            'Değer': customer.district || '-'
        }, {
            'Alan': 'Adres',
            'Değer': customer.address || '-'
        }, {
            'Alan': 'Müşteri Tipi',
            'Değer': customer.type === 'kurumsal' ? 'Kurumsal' : 'Bireysel'
        }, {
            'Alan': 'Para Birimi',
            'Değer': customer.currency === 'USD' ? 'Dolar (USD)' : 'Türk Lirası (TRY)'
        }, {
            'Alan': 'Vergi Dairesi',
            'Değer': customer.taxOffice || (customer.type === 'kurumsal' ? '-' : 'Bireysel müşteri')
        }, {
            'Alan': 'Vergi No',
            'Değer': customer.taxNumber || (customer.type === 'kurumsal' ? '-' : 'Bireysel müşteri')
        }, {
            'Alan': 'Güncel Bakiye',
            'Değer': canViewPrices ? `${(customer.balance || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***'
        }];

        // Prepare orders data
        const ordersData = orders.map(order => ({
            'Sipariş No': order.orderNumber || order.id.substring(0, 6),
            'Tarih': order.orderDate ? new Date(order.orderDate.seconds * 1000).toLocaleDateString('tr-TR') : '-',
            'Ürünler': (order.lineItems || []).map(item => `${item.productName || 'Bilinmeyen Ürün'} (${item.quantity} adet)`).join(', '),
            'Toplam Tutar': canViewPrices ? `${(order.total || 0).toFixed(2)} ${order.currency === 'USD' ? '$' : '₺'}` : '***',
            'Durum': order.status || 'Bilinmiyor'
        }));

        // Prepare transactions (payments) data
        const transactionsData = transactions.map(transaction => ({
            'Tarih': transaction.date ? new Date(transaction.date.seconds * 1000).toLocaleDateString('tr-TR') : '-',
            'İşlem Türü': transaction.type || 'Ödeme',
            'Açıklama': transaction.description || '-',
            'Tutar': canViewPrices ? `${(transaction.amount || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***',
            'Bakiye Etkisi': transaction.type === 'Tahsilat' ?
                (canViewPrices ? `+${(transaction.amount || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***') :
                (canViewPrices ? `-${(transaction.amount || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***')
        }));

        // Prepare invoices data
        const invoicesData = invoices.map(invoice => ({
            'Fatura No': invoice.id ? invoice.id.substring(0, 8).toUpperCase() : '-',
            'Tarih': invoice.invoiceDate ? new Date(invoice.invoiceDate.seconds * 1000).toLocaleDateString('tr-TR') : '-',
            'Ürünler': (invoice.lineItems || []).map(item => `${item.name || 'Bilinmeyen Ürün'} (${item.quantity} adet)`).join(', '),
            'Toplam Tutar': canViewPrices ? `${(invoice.total || 0).toFixed(2)} ${invoice.currency === 'USD' ? '$' : '₺'}` : '***',
            'Durum': invoice.status || 'Bilinmiyor'
        }));

        // Prepare balance history data
        const balanceHistoryData = balanceHistory.map(entry => ({
            'Tarih': entry.date ? new Date(entry.date.seconds * 1000).toLocaleDateString('tr-TR') : '-',
            'İşlem': entry.type || 'Bakiye Değişimi',
            'Açıklama': entry.description || '-',
            'Tutar': canViewPrices ? `${Math.abs(entry.amount || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***',
            'Yeni Bakiye': canViewPrices ? `${(entry.newBalance || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***'
        }));

        // Create worksheets
        const customerInfoWorksheet = XLSX.utils.json_to_sheet(customerInfo);
        const ordersWorksheet = XLSX.utils.json_to_sheet(ordersData);
        const transactionsWorksheet = XLSX.utils.json_to_sheet(transactionsData);
        const invoicesWorksheet = XLSX.utils.json_to_sheet(invoicesData); // Add invoices worksheet
        const balanceHistoryWorksheet = XLSX.utils.json_to_sheet(balanceHistoryData);

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, customerInfoWorksheet, 'Müşteri Bilgileri');
        XLSX.utils.book_append_sheet(workbook, ordersWorksheet, 'Siparişler');
        XLSX.utils.book_append_sheet(workbook, transactionsWorksheet, 'Ödemeler');
        XLSX.utils.book_append_sheet(workbook, invoicesWorksheet, 'Faturalar'); // Add invoices sheet
        XLSX.utils.book_append_sheet(workbook, balanceHistoryWorksheet, 'Bakiye Geçmişi');

        // Generate filename
        const fileName = `Musteri_${customer.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;

        // Export to Excel
        XLSX.writeFile(workbook, fileName);
    };

    const handleGenerateReconciliation = async (dateRange = null) => {
        if (!customer || !companyInfo) return;
        try {
            const getProductInfo = (productId) => {
                const product = products.find(p => p.id === productId);
                if (!product) return null;
                return { name: product.name, barcode: product.barcode || '-' };
            };

            let allMovements = [
                ...invoices.map(inv => ({
                    ...inv,
                    type: 'Fatura',
                    date: inv.invoiceDate,
                    amount: inv.total,
                    description: `Fatura No: ${inv.id.substring(0, 8).toUpperCase()}${inv.vatAmount > 0 ? `\nKDV İlavesi: +${inv.vatAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` : ''}\n\nÜrünler:\n${(inv.lineItems || [])
                        .map(item => {
                            const info = getProductInfo(item.productId);
                            const priceText = item.price ? `${Number(item.price).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}₺` : '';
                            return {
                                barcode: info?.barcode || item.barcode || '-',
                                name: item.name || info?.name || '-',
                                quantity: item.quantity,
                                priceText: priceText
                            };
                        })
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(item => `• ${item.name} (${item.quantity} adet x ${item.priceText})`)
                        .join('\n')}`
                })),
                ...transactions.map(t => {
                    if (t.type === 'return' || t.type === 'İade') {
                        // Try to enrich return description if it has items
                        // Note: return transactions usually store the summary description, but we might have relatedDocumentId
                        return t;
                    }
                    return t;
                })
            ];

            // For returns, we might need to fetch the actual return document if we want full line-item detail
            // But for now, let's at least ensure the existing description is safe

            // Tarih filtrelemesi pdfGenerator.js içerisinde devreden bakiye hesabı ile birlikte yapılmaktadır.

            allMovements.sort((a, b) => {
                const dateA = a.date?.seconds ? a.date.seconds * 1000 : new Date(a.date).getTime();
                const dateB = b.date?.seconds ? b.date.seconds * 1000 : new Date(b.date).getTime();
                return dateA - dateB;
            });

            await generateReconciliationPDF(companyInfo, customer, allMovements, dateRange, customer.lastReconciliationDate);
            
            // Update last reconciliation date
            const customerRef = doc(db, 'companies', userData.companyId, 'customers', customerId);
            await updateDoc(customerRef, {
                lastReconciliationDate: serverTimestamp()
            });
            
            setShowReconciliationModal(false);
        } catch (error) {
            console.error("Mutabakat oluşturulurken hata:", error);
            alert("Mutabakat belgesi oluşturulurken bir hata oluştu.");
        }
    };

    // Function to handle return creation
    const handleCreateReturn = async (returnData) => {
        try {
            const companyId = userData.companyId;

            // Use batch write to ensure atomicity
            const batch = writeBatch(db);

            // Create a single return record for all items
            const returnRef = doc(collection(db, 'companies', companyId, 'returns'));
            batch.set(returnRef, returnData);

            // Process each return item
            let totalReturnAmount = 0;

            for (const item of returnData.items) {
                totalReturnAmount += item.totalPrice;

                // 1. Update product stock (add returned quantity) - Only if productId exists
                if (item.productId) {
                    const productRef = doc(db, 'companies', companyId, 'products', item.productId);
                    const productDoc = await getDoc(productRef);

                    if (productDoc.exists()) {
                        const productData = productDoc.data();
                        const currentQuantity = productData.quantity || 0;
                        batch.update(productRef, {
                            quantity: currentQuantity + parseInt(item.quantity)
                        });

                        // 2. Add stock movement record for the return
                        const movementRef = doc(collection(productRef, 'movements'));
                        batch.set(movementRef, {
                            date: new Date(),
                            type: 'İade',
                            quantity: `+${item.quantity} ${productData.unit || 'Adet'}`,
                            description: `Müşteri: ${returnData.customerName}, Neden: ${item.reason || 'Belirtilmemiş'}`
                        });
                    }
                }
            }

            // 3. Update customer balance (ADD return amount since customer is returning products)
            const customerRef = doc(db, 'companies', companyId, 'customers', returnData.customerId);
            const customerDoc = await getDoc(customerRef);
            if (customerDoc.exists()) {
                const customerData = customerDoc.data();
                const currentBalance = customerData.balance || 0;
                const customerCurrency = customerData.currency || 'TRY';
                
                let balanceImpact = returnData.totalAmount;

                // Handle currency conversion if return currency differs from customer currency
                if (returnData.currency !== customerCurrency) {
                    const exchangeRates = companyInfo?.currencySettings || { USD: 33 }; // Default if not found
                    const usdRate = exchangeRates.USD || exchangeRates.rate || 33;

                    if (returnData.currency === 'USD' && customerCurrency === 'TRY') {
                        // Return is in USD, customer is in TRY
                        balanceImpact = returnData.totalAmount * usdRate;
                    } else if (returnData.currency === 'TRY' && customerCurrency === 'USD') {
                        // Return is in TRY, customer is in USD
                        balanceImpact = returnData.totalAmount / usdRate;
                    }
                }

                // When customer returns products, we should ADD the return amount to their balance
                batch.update(customerRef, {
                    balance: currentBalance + balanceImpact
                });

                // Add balance history record for the return
                const balanceHistoryRef = doc(collection(customerRef, 'balanceHistory'));
                batch.set(balanceHistoryRef, {
                    date: serverTimestamp(),
                    type: 'İade',
                    description: `İade alındı: ${returnData.items.length} ürün`,
                    amount: balanceImpact,
                    previousBalance: currentBalance,
                    newBalance: currentBalance + balanceImpact,
                    returnId: returnRef.id
                });
            }

            // 4. Add transaction record for the return
            const transactionRef = doc(collection(db, 'companies', companyId, 'transactions'));
            batch.set(transactionRef, {
                customerId: returnData.customerId,
                customerName: returnData.customerName,
                relatedPartyId: returnData.customerId, // Ensure this is set for customer history
                type: 'return',
                amount: returnData.totalAmount, // Store in the return currency
                currency: returnData.currency || 'TRY',
                description: `İade: ${returnData.items.length} ürün (${returnData.items.map(i => i.productName).join(', ')})`,
                date: serverTimestamp(),
                relatedDocumentId: returnRef.id,
                status: 'completed'
            });

            // Commit all changes
            await batch.commit();

            setShowReturnModal(false);
            alert('İade işlemi başarıyla kaydedildi!');
        } catch (error) {
            console.error("İade kaydedilirken hata oluştu:", error);
            alert('İade kaydedilirken bir hata oluştu: ' + error.message);
        }
    };

    // Function to cancel a return (reverse stock and balance changes)
    const handleCancelReturn = async (returnItem) => {
        if (!window.confirm('İade kaydını iptal etmek istediğinize emin misiniz? Stok ve bakiye değişiklikleri geri alınacaktır.')) return;
        try {
            const companyId = userData.companyId;
            const batch = writeBatch(db);

            // Reverse stock changes for each item
            for (const item of (returnItem.items || [])) {
                const productRef = doc(db, 'companies', companyId, 'products', item.productId);
                const productDoc = await getDoc(productRef);
                if (productDoc.exists()) {
                    const currentQty = productDoc.data().quantity || 0;
                    batch.update(productRef, { quantity: Math.max(0, currentQty - parseInt(item.quantity)) });

                    // Add reverse movement record
                    const movementRef = doc(collection(productRef, 'movements'));
                    batch.set(movementRef, {
                        date: new Date(),
                        type: 'İade İptali',
                        quantity: `-${item.quantity} ${productDoc.data().unit || 'Adet'}`,
                        description: `İade iptali - Müşteri: ${returnItem.customerName}`
                    });
                }
            }

            // Reverse balance change
            const customerRef = doc(db, 'companies', companyId, 'customers', returnItem.customerId);
            const customerDoc = await getDoc(customerRef);
            if (customerDoc.exists()) {
                const currentBalance = customerDoc.data().balance || 0;
                batch.update(customerRef, { balance: currentBalance - (returnItem.totalAmount || 0) });
            }

            // Mark return as cancelled
            const returnRef = doc(db, 'companies', companyId, 'returns', returnItem.id);
            batch.update(returnRef, { status: 'cancelled', cancelledAt: serverTimestamp() });

            // Add reverse transaction record
            const txRef = doc(collection(db, 'companies', companyId, 'transactions'));
            batch.set(txRef, {
                customerId: returnItem.customerId,
                customerName: returnItem.customerName,
                type: 'return_cancel',
                amount: returnItem.totalAmount || 0,
                description: `İade İptali: ${(returnItem.items || []).map(i => i.productName).join(', ')}`,
                date: serverTimestamp(),
                relatedDocumentId: returnItem.id,
                status: 'completed'
            });

            await batch.commit();
            setExpandedReturnId(null);
            alert('İade başarıyla iptal edildi!');
        } catch (error) {
            console.error('İade iptal edilirken hata:', error);
            alert('İade iptal edilirken hata oluştu: ' + error.message);
        }
    };

    const handleInviteToPortal = async () => {
        if (!customer.email) {
            alert('Portala davet etmek için müşterinin e-posta adresi kayıtlı olmalıdır.');
            return;
        }

        if (!window.confirm(`${customer.name} müşterisini Müşteri Portalı'na davet etmek istediğinizden emin misiniz?`)) {
            return;
        }

        setInvitationLoading(true);
        try {
            const companyId = userData.companyId;
            const invitationRef = doc(collection(db, 'companies', companyId, 'invitations'));

            const invitationData = {
                email: customer.email,
                role: 'customer',
                customerId: customerId,
                customerName: customer.name,
                status: 'pending',
                createdAt: serverTimestamp(),
                expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days
            };

            // Correction: invitationRef is doc(collection(...)) which creates a new ref with unique ID.
            const { setDoc } = await import('firebase/firestore'); // ensure setDoc is available if not imported
            await setDoc(invitationRef, invitationData);

            // Call Cloud Function to send email
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const functions = getFunctions();
            const sendEmail = httpsCallable(functions, 'sendCustomerPortalInvitationEmail');

            const invitationLink = `${window.location.origin}/register-personnel/${companyId}/${invitationRef.id}`; // Reusing personnel register page for now

            await sendEmail({
                email: customer.email,
                invitationLink,
                companyName: userData.companyName || 'Şirketimiz',
                customerName: customer.name
            });

            setInvitationSent(true);
            alert('Müşteri Portalı davetiyesi başarıyla gönderildi.');
        } catch (error) {
            console.error("Portal davetiyesi gönderilirken hata:", error);
            alert("Davetiye gönderilirken bir hata oluştu.");
        } finally {
            setInvitationLoading(false);
        }
    };

    // Inject custom scrollbar styles
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = scrollbarCSS;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        if (!userData?.companyId || !customerId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        const customerRef = doc(db, 'companies', companyId, 'customers', customerId);
        const unsubCustomer = onSnapshot(customerRef, (docSnap) => {
            if (docSnap.exists()) {
                const customerData = { id: docSnap.id, ...docSnap.data() };
                setCustomer(customerData);
                setEditForm(customerData);
            }
            setLoading(false);
        });

        const ordersQuery = query(collection(db, 'companies', companyId, 'orders'), where('customerId', '==', customerId), orderBy('orderDate', 'desc'));
        const unsubOrders = onSnapshot(ordersQuery, (snapshot) => {
            const ordersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setOrders(ordersData);
        });

        const transactionsQuery = query(collection(db, 'companies', companyId, 'transactions'), where('relatedPartyId', '==', customerId), orderBy('date', 'desc'));
        const unsubTransactions = onSnapshot(transactionsQuery, (snapshot) => {
            const transactionsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTransactions(transactionsData);
        });

        // Fetch invoices for this customer
        const invoicesQuery = query(collection(db, 'companies', companyId, 'invoices'), where('customerId', '==', customerId), orderBy('invoiceDate', 'desc'));
        const unsubInvoices = onSnapshot(invoicesQuery, (snapshot) => {
            const invoicesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setInvoices(invoicesData);
        });

        // Fetch balance history
        const balanceHistoryQuery = query(collection(db, 'companies', companyId, 'customers', customerId, 'balanceHistory'), orderBy('date', 'desc'));
        const unsubBalanceHistory = onSnapshot(balanceHistoryQuery, (snapshot) => {
            const balanceHistoryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setBalanceHistory(balanceHistoryData);
        });

        // Fetch all products
        const productsQuery = query(collection(db, 'companies', companyId, 'products'));
        const unsubProducts = onSnapshot(productsQuery, (snapshot) => {
            const productsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProducts(productsData);
        });

        // Fetch returns for this customer
        const returnsQuery = query(collection(db, 'companies', companyId, 'returns'), where('customerId', '==', customerId), orderBy('createdAt', 'desc'));
        const unsubReturns = onSnapshot(returnsQuery, (snapshot) => {
            const returnsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setReturns(returnsData);
        });

        // Fetch company info
        const companyRef = doc(db, 'companies', companyId);
        const unsubCompany = onSnapshot(companyRef, (docSnap) => {
            if (docSnap.exists()) {
                setCompanyInfo(docSnap.data());
            }
        });

        return () => {
            unsubCustomer();
            unsubOrders();
            unsubTransactions();
            unsubInvoices(); // Add invoices unsubscribe
            unsubBalanceHistory(); // Add balance history unsubscribe
            unsubProducts(); // Add products unsubscribe
            unsubReturns(); // Add returns unsubscribe
            unsubCompany();
        };
    }, [userData, customerId]);

    // Fetch linked supplier if customer is also a supplier (for unified balance view)
    useEffect(() => {
        if (!userData?.companyId || !customer?.name) return;

        const companyId = userData.companyId;
        const supplierQuery = query(collection(db, 'companies', companyId, 'suppliers'), where('name', '==', customer.name));
        const unsubSupplier = onSnapshot(supplierQuery, (snapshot) => {
            if (!snapshot.empty) {
                setLinkedSupplier({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
            } else {
                setLinkedSupplier(null);
            }
        });

        return () => unsubSupplier();
    }, [userData, customer?.name]);

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        if (name === 'customerGroupId') {
            const selectedGroup = customerGroups.find(g => g.id === value);
            setEditForm(prev => ({ ...prev, customerGroupId: value, customerGroupName: selectedGroup?.name || '' }));
        } else {
            setEditForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async () => {
        const customerRef = doc(db, 'companies', userData.companyId, 'customers', customerId);
        try {
            await updateDoc(customerRef, editForm);
            setEditing(false);
            toast.success("Müşteri bilgileri güncellendi.");
        } catch (error) {
            console.error("Müşteri güncellenirken hata:", error);
            toast.error("Müşteri güncellenirken bir hata oluştu.");
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Bu müşteriyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz ve müşteriye bağlı tüm veriler etkilenebilir.')) return;
        const customerRef = doc(db, 'companies', userData.companyId, 'customers', customerId);
        try {
            await deleteDoc(customerRef);
            navigate('/musteriler');
        } catch (error) {
            console.error("Müşteri silinirken hata:", error);
        }
    };


    // Get customer-specific prices for display
    const getCustomerPricesForProduct = (productId) => {
        if (customerPrices[customerId]) {
            return customerPrices[customerId][productId] || null;
        }
        return null;
    };

    if (loading) return <p>Yükleniyor...</p>;
    if (!customer) return <h2>Müşteri Bulunamadı</h2>;

    const balanceStyle = customer.balance >= 0 ? styles.balancePositive : styles.balanceNegative;

    // Calculate Summary Stats
    const totalSales = invoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    const openOrdersCount = orders.filter(o => o.status !== 'Tamamlandı' && o.status !== 'İptal Edildi' && o.status !== 'İade Edildi').length;

    // Find last transaction date
    let lastTransactionDate = '-';
    if (transactions.length > 0) {
        const lastTx = transactions[0]; // Assuming ordered by date desc
        lastTransactionDate = new Date(lastTx.date.seconds * 1000).toLocaleDateString('tr-TR');
    }

    const StatCard = ({ title, value, icon, color, subValue }) => (
        <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            border: '1px solid #f0f0f0'
        }}>
            <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: `${color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                marginRight: '15px',
                color: color
            }}>
                {icon}
            </div>
            <div>
                <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '4px' }}>{title}</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>{value}</div>
                {subValue && <div style={{ fontSize: '12px', color: '#94a3b8' }}>{subValue}</div>}
            </div>
        </div>
    );

    return (
        <div style={{ ...styles.container, padding: '20px' }}>
            <style>{scrollbarCSS}</style>
            
            {/* Header */}
            <div style={styles.header}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Link to="/musteriler" style={{ ...styles.backLink, marginBottom: 0 }}>&larr;</Link>
                        <h1 style={{ ...styles.title, marginBottom: 0 }}>{customer.name}</h1>
                        <span style={{
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            background: customer.type === 'kurumsal' ? '#e0e7ff' : '#f0fdf4',
                            color: customer.type === 'kurumsal' ? '#3730a3' : '#166534',
                            fontWeight: '500'
                        }}>
                            {customer.type === 'kurumsal' ? 'Kurumsal' : 'Bireysel'}
                        </span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={() => navigate('/siparis-olustur', { state: { customerId: customer.id } })}
                        style={{ ...styles.button, background: '#06C167', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        <span>+</span> Yeni Sipariş
                    </button>
                    <button
                        onClick={() => navigate('/kasa', { state: { action: 'add_collection', customerId: customer.id, customerName: customer.name } })}
                        style={{ ...styles.button, background: '#198754', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        <span>₺</span> Tahsilat Ekle
                    </button>
                    <button
                        onClick={handleInviteToPortal}
                        disabled={invitationLoading || invitationSent}
                        style={{
                            ...styles.invitationButton,
                            opacity: (invitationLoading || invitationSent) ? 0.7 : 1,
                            cursor: (invitationLoading || invitationSent) ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <span>👤</span> {invitationLoading ? 'Gönderiliyor...' : invitationSent ? 'Davet Edildi' : 'Portala Davet Et'}
                    </button>
                    {!editing && (
                        <div style={{ display: 'flex', background: 'white', border: '1px solid #dee2e6', borderRadius: '6px', padding: '2px' }}>
                            <button onClick={() => setEditing(true)} style={{ ...styles.buttonIcon, color: '#495057' }} title="Düzenle">✏️</button>
                            <div style={{ width: '1px', background: '#dee2e6', margin: '2px 0' }}></div>
                            <button onClick={() => setShowReconciliationModal(true)} style={{ ...styles.buttonIcon, color: '#06C167' }} title="Mutabakat Belgesi (PDF)">📋</button>
                            <div style={{ width: '1px', background: '#dee2e6', margin: '2px 0' }}></div>
                            <button onClick={handleExportCustomerData} style={{ ...styles.buttonIcon, color: '#198754' }} title="Excel">📊</button>
                            <div style={{ width: '1px', background: '#dee2e6', margin: '2px 0' }}></div>
                            <button onClick={handleDelete} style={{ ...styles.buttonIcon, color: '#dc3545' }} title="Sil">🗑️</button>
                        </div>
                    )}
                    {editing && (
                        <>
                            <button onClick={() => setEditing(false)} style={{ ...styles.button, background: '#6c757d' }}>İptal</button>
                            <button onClick={handleSave} style={{ ...styles.button, background: '#198754' }}>Kaydet</button>
                        </>
                    )}
                </div>
            </div>

            {/* Summary Dashboard */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                {linkedSupplier ? (
                    <>
                        <StatCard
                            title="Müşteri Bakiyesi"
                            value={canViewPrices ? `${(customer.balance || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***'}
                            icon="👤"
                            color={customer.balance >= 0 ? '#3b82f6' : '#ef4444'}
                            subValue={customer.balance >= 0 ? 'Bize Borcu Var' : 'Alacaklı'}
                        />
                        <StatCard
                            title="Tedarikçi Bakiyesi"
                            value={canViewPrices ? `${(linkedSupplier.balance || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***'}
                            icon="🏭"
                            color={linkedSupplier.balance >= 0 ? '#f59e0b' : '#10b981'}
                            subValue={linkedSupplier.balance >= 0 ? 'Bizim Borcumuz Var' : 'Alacaklıyız'}
                        />
                        <StatCard
                            title="Net Bakiye (Kalan)"
                            value={canViewPrices ? `${Math.abs((customer.balance || 0) - (linkedSupplier.balance || 0)).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***'}
                            icon="⚖️"
                            color={((customer.balance || 0) - (linkedSupplier.balance || 0)) >= 0 ? '#10b981' : '#ef4444'}
                            subValue={((customer.balance || 0) - (linkedSupplier.balance || 0)) >= 0 ? 'Bize Borcu Var' : 'Bizim Borcumuz Var'}
                        />
                    </>
                ) : (
                    <StatCard
                        title="Net Bakiye"
                        value={canViewPrices ? `${(customer.balance || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***'}
                        icon="💰"
                        color={customer.balance >= 0 ? '#10b981' : '#ef4444'}
                        subValue={customer.balance >= 0 ? 'Bize Borcu Var' : 'Bizim Borcumuz Var'}
                    />
                )}
                <StatCard
                    title="Toplam Ciro"
                    value={canViewPrices ? `${totalSales.toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***'}
                    icon="📈"
                    color="#3b82f6"
                    subValue={`${invoices.length} fatura`}
                />
                <StatCard
                    title="Açık Siparişler"
                    value={openOrdersCount}
                    icon="📦"
                    color="#f59e0b"
                    subValue="Devam eden işler"
                />
                <StatCard
                    title="Son İşlem"
                    value={lastTransactionDate}
                    icon="📅"
                    color="#8b5cf6"
                    subValue="Son aktivite"
                />
            </div>
            <div style={styles.grid}>
                <div style={styles.box}>
                    <h3>Müşteri Bilgileri</h3>
                    <ul style={styles.infoList}>
                        <li style={styles.infoItem}><span style={styles.infoLabel}>Ad Soyad / Unvan</span> <span style={styles.infoValue}>{editing ? <input style={styles.input} name="name" value={editForm.name} onChange={handleEditChange} /> : customer.name}</span></li>
                        <li style={styles.infoItem}><span style={styles.infoLabel}>E-posta</span> <span style={styles.infoValue}>{editing ? <input style={styles.input} name="email" value={editForm.email} onChange={handleEditChange} /> : customer.email || '-'}</span></li>
                        <li style={styles.infoItem}><span style={styles.infoLabel}>Telefon</span> <span style={styles.infoValue}>{editing ? <input style={styles.input} name="phone" value={editForm.phone} onChange={handleEditChange} /> : customer.phone || '-'}</span></li>
                        <li style={styles.infoItem}><span style={styles.infoLabel}>İl</span> <span style={styles.infoValue}>{editing ? <input style={styles.input} name="city" value={editForm.city} onChange={handleEditChange} /> : customer.city || '-'}</span></li>
                        <li style={styles.infoItem}><span style={styles.infoLabel}>İlçe</span> <span style={styles.infoValue}>{editing ? <input style={styles.input} name="district" value={editForm.district} onChange={handleEditChange} /> : customer.district || '-'}</span></li>
                        <li style={styles.infoItem}><span style={styles.infoLabel}>Adres</span> <span style={styles.infoValue}>{editing ? <input style={styles.input} name="address" value={editForm.address} onChange={handleEditChange} /> : customer.address || '-'}</span></li>
                        <li style={styles.infoItem}><span style={styles.infoLabel}>Müşteri Grubu</span> <span style={styles.infoValue}>{editing ? (
                            <select style={styles.select} name="customerGroupId" value={editForm.customerGroupId || ''} onChange={handleEditChange}>
                                <option value="">Grup Seçin</option>
                                {customerGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                            </select>
                        ) : (
                            customer.customerGroupName ? (
                                <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{customer.customerGroupName}</span>
                            ) : '-'
                        )}</span></li>
                        {customer.type === 'kurumsal' && (
                            <>
                                <li style={styles.infoItem}><span style={styles.infoLabel}>Vergi Dairesi</span> <span style={styles.infoValue}>{editing ? <input style={styles.input} name="taxOffice" value={editForm.taxOffice} onChange={handleEditChange} /> : customer.taxOffice || '-'}</span></li>
                                <li style={styles.infoItem}><span style={styles.infoLabel}>Vergi No</span> <span style={styles.infoValue}>{editing ? <input style={styles.input} name="taxNumber" value={editForm.taxNumber} onChange={handleEditChange} /> : customer.taxNumber || '-'}</span></li>
                            </>
                        )}
                    </ul>
                    <div style={{ ...styles.balance, ...balanceStyle }}>
                        {canViewPrices ? `${(customer.balance || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***'}
                        <div style={{ fontSize: '14px', fontWeight: 'normal', color: '#6c757d' }}>Güncel Bakiye</div>
                    </div>
                </div>
                <div>
                    <div style={styles.tabs}>
                        <button
                            style={{ ...styles.tab, ...(activeTab === 'info' && styles.activeTab) }}
                            onClick={() => setActiveTab('info')}
                        >
                            Müşteri Bilgileri
                        </button>
                        <button
                            style={{ ...styles.tab, ...(activeTab === 'orders' && styles.activeTab) }}
                            onClick={() => setActiveTab('orders')}
                        >
                            Siparişler
                        </button>
                        <button
                            style={{ ...styles.tab, ...(activeTab === 'invoices' && styles.activeTab) }}
                            onClick={() => setActiveTab('invoices')}
                        >
                            Faturalar
                        </button>

                        <button
                            style={{ ...styles.tab, ...(activeTab === 'returns' && styles.activeTab) }}
                            onClick={() => setActiveTab('returns')}
                        >
                            İadeler
                        </button>
                        <button
                            style={{ ...styles.tab, ...(activeTab === 'transactions' && styles.activeTab) }}
                            onClick={() => setActiveTab('transactions')}
                        >
                            Ödeme Geçmişi
                        </button>
                        <button
                            style={{ ...styles.tab, ...(activeTab === 'balance' && styles.activeTab) }}
                            onClick={() => setActiveTab('balance')}
                        >
                            Bakiye Geçmişi
                        </button>
                        <button
                            style={{ ...styles.tab, ...(activeTab === 'pricing' && styles.activeTab) }}
                            onClick={() => navigate(`/musteriler/${customerId}/fiyatlar`)}
                        >
                            Özel Fiyatlar
                        </button>
                    </div>

                    {activeTab === 'info' && (
                        <div style={styles.tabContent}>
                            <h3 style={styles.tabTitle}>Genel Bilgiler</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                    <h4 style={{ margin: '0 0 15px 0', color: '#64748b', fontSize: '14px', textTransform: 'uppercase' }}>Kimlik & İletişim</h4>
                                    <ul style={styles.infoList}>
                                        <li style={styles.infoItem}><span style={styles.infoLabel}>Müşteri Tipi</span><span style={styles.infoValue}>{customer.type === 'bireysel' ? 'Bireysel' : 'Kurumsal'}</span></li>
                                        <li style={styles.infoItem}><span style={styles.infoLabel}>Ad Soyad</span><span style={styles.infoValue}>{customer.name}</span></li>
                                        <li style={styles.infoItem}><span style={styles.infoLabel}>E-posta</span><span style={styles.infoValue}>{customer.email || '-'}</span></li>
                                        <li style={styles.infoItem}><span style={styles.infoLabel}>Telefon</span><span style={styles.infoValue}>{customer.phone || '-'}</span></li>
                                    </ul>
                                </div>
                                <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                    <h4 style={{ margin: '0 0 15px 0', color: '#64748b', fontSize: '14px', textTransform: 'uppercase' }}>Finansal & Adres</h4>
                                    <ul style={styles.infoList}>
                                        <li style={styles.infoItem}><span style={styles.infoLabel}>Para Birimi</span><span style={styles.infoValue}>{customer.currency === 'USD' ? 'Dolar' : 'Türk Lirası'}</span></li>
                                        <li style={styles.infoItem}><span style={styles.infoLabel}>Vergi Dairesi</span><span style={styles.infoValue}>{customer.taxOffice || '-'}</span></li>
                                        <li style={styles.infoItem}><span style={styles.infoLabel}>Vergi No</span><span style={styles.infoValue}>{customer.taxNumber || '-'}</span></li>
                                        <li style={styles.infoItem}><span style={styles.infoLabel}>Adres</span><span style={styles.infoValue}>{customer.address || '-'} / {customer.district} / {customer.city}</span></li>
                                    </ul>
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                                <Link
                                    to={`/musteriler/${customerId}/fiyatlar`}
                                    style={{
                                        ...styles.button,
                                        background: '#10b981',
                                        textDecoration: 'none',
                                        display: 'inline-block'
                                    }}
                                >
                                    Özel Fiyatları Yönet
                                </Link>
                            </div>
                        </div>
                    )}

                    {activeTab === 'orders' && (
                        <div style={styles.tabContent}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={styles.tabTitle}>Siparişler</h3>
                                <button
                                    onClick={() => setShowReturnModal(true)}
                                    style={{ ...styles.button, background: '#dc3545' }}
                                >
                                    + Yeni İade
                                </button>
                            </div>

                            {/* Open/Closed Order Tabs */}
                            <div style={{ display: 'flex', marginBottom: '20px' }}>
                                <button
                                    style={{
                                        ...styles.tab,
                                        borderBottom: '2px solid transparent',
                                        marginRight: '10px',
                                        ...(orderSubTab === 'open' && { borderBottom: '2px solid #06C167', color: '#06C167' })
                                    }}
                                    onClick={() => setOrderSubTab('open')}
                                >
                                    Açık Siparişler
                                </button>
                                <button
                                    style={{
                                        ...styles.tab,
                                        borderBottom: '2px solid transparent',
                                        ...(orderSubTab === 'closed' && { borderBottom: '2px solid #06C167', color: '#06C167' })
                                    }}
                                    onClick={() => setOrderSubTab('closed')}
                                >
                                    Kapatılan Siparişler
                                </button>
                            </div>
                            {(() => {
                                // Filter orders based on sub-tab
                                const filteredOrders = orderSubTab === 'open'
                                    ? orders.filter(order =>
                                        order.status !== 'Tamamlandı' &&
                                        order.status !== 'İade Edildi' &&
                                        order.status !== 'İptal Edildi'
                                    )
                                    : orders.filter(order =>
                                        order.status === 'Tamamlandı' ||
                                        order.status === 'İade Edildi' ||
                                        order.status === 'İptal Edildi'
                                    );

                                return filteredOrders.length === 0 ? (
                                    <div style={styles.emptyState}>
                                        <div style={styles.emptyIcon}>📦</div>
                                        <div style={styles.emptyText}>
                                            {orderSubTab === 'open'
                                                ? 'Henüz açık sipariş bulunmuyor'
                                                : 'Henüz kapatılan sipariş bulunmuyor'}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ ...styles.tableContainer, ...styles.scrollableContainer }} className="scrollable-container">
                                        <table style={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th style={styles.th}>Sipariş No</th>
                                                    <th style={styles.th}>Tarih</th>
                                                    <th style={styles.th}>Ürünler</th>
                                                    <th style={styles.th}>Toplam</th>
                                                    <th style={styles.th}>Durum</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredOrders.map(order => (
                                                    <tr
                                                        key={order.id}
                                                        style={styles.trHover}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            navigate(`/siparisler/${order.id}`);
                                                        }}
                                                    >
                                                        <td style={styles.td}>{order.orderNumber}</td>
                                                        <td style={styles.td}>{new Date(order.orderDate?.toDate?.() || order.orderDate).toLocaleDateString('tr-TR')}</td>
                                                        <td style={styles.td}>
                                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                {order.lineItems.slice(0, 2).map((item, index) => (
                                                                    <div key={index} style={{ marginBottom: '4px' }}>
                                                                        <span>{item.productName}</span>
                                                                        <span style={{ marginLeft: '8px', color: '#6c757d' }}>
                                                                            {item.quantity} x {canViewPrices ? formatCurrency(item.price, order.currency) : '***'}
                                                                            {getCustomerPricesForProduct(item.productId) && (
                                                                                <span style={{ color: '#059669', marginLeft: '4px' }}>
                                                                                    (Özel: {canViewPrices ? formatCurrency(getCustomerPricesForProduct(item.productId), order.currency) : '***'})
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                                {order.lineItems.length > 2 && (
                                                                    <span style={{ color: '#6c757d', fontSize: '12px' }}>
                                                                        +{order.lineItems.length - 2} daha fazla
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={{ ...styles.td, fontWeight: '600' }}>
                                                            {canViewPrices ? formatCurrency(order.total, order.currency) : '***'}
                                                        </td>
                                                        <td style={styles.td}>
                                                            <span style={{
                                                                ...styles.statusBadge,
                                                                ...(order.status === 'Sipariş Alındı' && { background: '#dbeafe', color: '#1d4ed8' }) ||
                                                                (order.status === 'Hazırlanıyor' && { background: '#ffedd5', color: '#ea580c' }) ||
                                                                (order.status === 'Kargoda' && { background: '#dcfce7', color: '#06C167' }) ||
                                                                (order.status === 'Tamamlandı' && { background: '#f0fdf4', color: '#06C167' }) ||
                                                                (order.status === 'İade Edildi' && { background: '#fee2e2', color: '#b91c1c' }) ||
                                                                (order.status === 'İptal Edildi' && { background: '#e5e7eb', color: '#374151' }) ||
                                                                { background: '#f3f4f6', color: '#374151' }
                                                            }}>
                                                                {order.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {activeTab === 'invoices' && (
                        <div style={styles.tabContent}>
                            <h3 style={styles.tabTitle}>Faturalar</h3>
                            {invoices.length === 0 ? (
                                <div style={styles.emptyState}>
                                    <div style={styles.emptyIcon}>📄</div>
                                    <div style={styles.emptyText}>Henüz fatura bulunmuyor</div>
                                </div>
                            ) : (
                                <div style={{ ...styles.tableContainer, ...styles.scrollableContainer }} className="scrollable-container">
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={styles.th}>Fatura No</th>
                                                <th style={styles.th}>Tarih</th>
                                                <th style={styles.th}>Vade</th>
                                                <th style={styles.th}>Tutar</th>
                                                <th style={styles.th}>Durum</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoices.map(inv => (
                                                <tr 
                                                    key={inv.id} 
                                                    style={styles.trHover}
                                                    onClick={() => setSelectedInvoice(inv)}
                                                >
                                                    <td style={styles.td}>{inv.invoiceNumber || inv.id.substring(0,6)}</td>
                                                    <td style={styles.td}>{inv.invoiceDate?.seconds ? new Date(inv.invoiceDate.seconds * 1000).toLocaleDateString('tr-TR') : '-'}</td>
                                                    <td style={styles.td}>{inv.dueDate?.seconds ? new Date(inv.dueDate.seconds * 1000).toLocaleDateString('tr-TR') : '-'}</td>
                                                    <td style={{ ...styles.td, fontWeight: '600' }}>
                                                        {canViewPrices ? `${(inv.total || 0).toFixed(2)} ${inv.currency === 'USD' ? '$' : '₺'}` : '***'}
                                                    </td>
                                                    <td style={styles.td}>
                                                        <span style={{
                                                            ...styles.statusBadge,
                                                            ...(inv.status === 'Tamamlandı' && { background: '#dcfce7', color: '#166534' }),
                                                            ...(inv.status === 'Beklemede' && { background: '#fef3c7', color: '#92400e' }),
                                                            ...(inv.status === 'Taslak' && { background: '#f3f4f6', color: '#374151' })
                                                        }}>
                                                            {inv.status || 'Taslak'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}


                    {activeTab === 'returns' && (() => {
                        const reasonLabels = {
                            hatali_urun: 'Hatalı Ürün', kargo_hasari: 'Kargo Hasarı',
                            degistirme: 'Değiştirme', istenmeyen_urun: 'İstenmeyen Ürün', diger: 'Diğer'
                        };
                        const reasonBadgeColors = {
                            hatali_urun: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
                            kargo_hasari: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
                            degistirme: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
                            istenmeyen_urun: { bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe' },
                            diger: { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' }
                        };
                        const getReasonBadge = (reason) => {
                            const c = reasonBadgeColors[reason] || reasonBadgeColors.diger;
                            return { padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', background: c.bg, color: c.color, border: `1px solid ${c.border}`, display: 'inline-block' };
                        };
                        const statusBadgeMap = {
                            completed: { bg: '#f0fdf4', color: '#06C167', border: '#bbf7d0', label: 'Tamamlandı' },
                            cancelled: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', label: 'İptal Edildi' }
                        };

                        // Filter returns by date
                        const now = new Date();
                        const filteredReturns = returns.filter(r => {
                            if (returnDateFilter === 'all') return true;
                            if (!r.createdAt?.toDate) return false;
                            const d = r.createdAt.toDate();
                            if (returnDateFilter === '7') return (now - d) <= 7 * 86400000;
                            if (returnDateFilter === '30') return (now - d) <= 30 * 86400000;
                            if (returnDateFilter === '90') return (now - d) <= 90 * 86400000;
                            return true;
                        });

                        // Stats
                        const activeReturns = returns.filter(r => r.status !== 'cancelled');
                        const totalReturnCount = activeReturns.length;
                        const totalReturnAmount = activeReturns.reduce((s, r) => s + (r.totalAmount || 0), 0);
                        const totalReturnedItems = activeReturns.reduce((s, r) => s + (r.items || []).reduce((a, i) => a + (i.quantity || 0), 0), 0);

                        // Most returned reason
                        const reasonCounts = {};
                        activeReturns.forEach(r => {
                            (r.items || []).forEach(item => {
                                const reason = item.reason || 'diger';
                                reasonCounts[reason] = (reasonCounts[reason] || 0) + (item.quantity || 0);
                            });
                        });
                        const topReason = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1])[0];

                        const statCardStyle = (gradient) => ({
                            flex: 1, padding: '20px', borderRadius: '14px', background: gradient,
                            border: '1px solid rgba(255,255,255,0.3)', minWidth: '150px'
                        });

                        return (
                            <div style={styles.tabContent}>
                                {/* Header */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h3 style={{ ...styles.tabTitle, margin: 0 }}>Ürün İadeleri</h3>
                                    <button onClick={() => setShowReturnModal(true)}
                                        style={{ ...styles.button, background: 'linear-gradient(135deg, #dc2626, #ef4444)', borderRadius: '10px', padding: '10px 20px', fontWeight: '600', border: 'none', color: 'white', cursor: 'pointer', fontSize: '14px' }}>
                                        + Yeni İade
                                    </button>
                                </div>

                                {/* Stats Cards */}
                                {returns.length > 0 && (
                                    <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                                        <div style={statCardStyle('linear-gradient(135deg, #eff6ff, #dbeafe)')}>
                                            <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '600', marginBottom: '4px' }}>İade Sayısı</div>
                                            <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e40af' }}>{totalReturnCount}</div>
                                        </div>
                                        <div style={statCardStyle('linear-gradient(135deg, #fef2f2, #fecaca)')}>
                                            <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600', marginBottom: '4px' }}>İade Tutarı</div>
                                            <div style={{ fontSize: '28px', fontWeight: '700', color: '#b91c1c' }}>
                                                {canViewPrices ? `${totalReturnAmount.toFixed(2)} ${customer?.currency === 'USD' ? '$' : '₺'}` : '***'}
                                            </div>
                                        </div>
                                        <div style={statCardStyle('linear-gradient(135deg, #f0fdf4, #dcfce7)')}>
                                            <div style={{ fontSize: '13px', color: '#06C167', fontWeight: '600', marginBottom: '4px' }}>İade Edilen Ürün</div>
                                            <div style={{ fontSize: '28px', fontWeight: '700', color: '#06C167' }}>{totalReturnedItems} adet</div>
                                        </div>
                                        <div style={statCardStyle('linear-gradient(135deg, #fefce8, #fef08a)')}>
                                            <div style={{ fontSize: '13px', color: '#ca8a04', fontWeight: '600', marginBottom: '4px' }}>En Sık Neden</div>
                                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#a16207' }}>
                                                {topReason ? reasonLabels[topReason[0]] || topReason[0] : '-'}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {returns.length === 0 ? (
                                    <div style={styles.emptyState}>
                                        <div style={styles.emptyIcon}>📦</div>
                                        <div style={styles.emptyText}>Henüz ürün iadesi bulunmuyor</div>
                                        <button onClick={() => setShowReturnModal(true)}
                                            style={{ ...styles.button, marginTop: '20px' }}>
                                            İlk İadeyi Kaydet
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        {/* Date Filter */}
                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                            {[{ value: 'all', label: 'Tümü' }, { value: '7', label: 'Son 7 Gün' }, { value: '30', label: 'Son 30 Gün' }, { value: '90', label: 'Son 90 Gün' }].map(opt => (
                                                <button key={opt.value} onClick={() => setReturnDateFilter(opt.value)}
                                                    style={{
                                                        padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s',
                                                        border: returnDateFilter === opt.value ? '1px solid #06C167' : '1px solid #e2e8f0',
                                                        background: returnDateFilter === opt.value ? '#eef2ff' : 'white',
                                                        color: returnDateFilter === opt.value ? '#4f46e5' : '#64748b'
                                                    }}>
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Returns List */}
                                        <div style={{ ...styles.scrollableContainer }} className="scrollable-container">
                                            {filteredReturns.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Bu tarih aralığında iade bulunmuyor</div>
                                            ) : (
                                                filteredReturns.map(returnItem => {
                                                    const isExpanded = expandedReturnId === returnItem.id;
                                                    const isCancelled = returnItem.status === 'cancelled';
                                                    const sBadge = statusBadgeMap[returnItem.status] || statusBadgeMap.completed;
                                                    const itemCount = (returnItem.items || []).reduce((s, i) => s + (i.quantity || 0), 0);

                                                    return (
                                                        <div key={returnItem.id} style={{
                                                            background: isCancelled ? '#fafafa' : 'white', borderRadius: '14px', border: `1px solid ${isCancelled ? '#e5e7eb' : '#e2e8f0'}`,
                                                            marginBottom: '12px', overflow: 'hidden', transition: 'all 0.2s',
                                                            opacity: isCancelled ? 0.7 : 1, boxShadow: isExpanded ? '0 4px 16px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.04)'
                                                        }}>
                                                            {/* Summary Row */}
                                                            <div onClick={() => setExpandedReturnId(isExpanded ? null : returnItem.id)}
                                                                style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 1.5fr 1fr auto', gap: '16px', padding: '16px 20px', cursor: 'pointer', alignItems: 'center', transition: 'background 0.15s' }}
                                                                onMouseEnter={e => { if (!isCancelled) e.currentTarget.style.background = '#f8fafc'; }}
                                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                                <div>
                                                                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px', marginBottom: '2px' }}>
                                                                        {(returnItem.items || []).slice(0, 2).map(i => i.productName).join(', ')}
                                                                        {(returnItem.items || []).length > 2 && <span style={{ color: '#94a3b8' }}> +{(returnItem.items || []).length - 2}</span>}
                                                                    </div>
                                                                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                                                                        {(returnItem.items || []).length} kalem
                                                                    </div>
                                                                </div>
                                                                <div style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>{itemCount} adet</div>
                                                                <div style={{ fontSize: '14px', fontWeight: '600', color: isCancelled ? '#9ca3af' : '#1e293b' }}>
                                                                    {canViewPrices ? `${(returnItem.totalAmount || 0).toFixed(2)} ${customer?.currency === 'USD' ? '$' : '₺'}` : '***'}
                                                                </div>
                                                                <div>
                                                                    <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', background: sBadge.bg, color: sBadge.color, border: `1px solid ${sBadge.border}` }}>
                                                                        {sBadge.label}
                                                                    </span>
                                                                </div>
                                                                <div style={{ fontSize: '13px', color: '#64748b' }}>
                                                                    {returnItem.createdAt?.toDate ? returnItem.createdAt.toDate().toLocaleDateString('tr-TR') : '-'}
                                                                </div>
                                                                <div style={{ fontSize: '18px', color: '#94a3b8', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>▼</div>
                                                            </div>

                                                            {/* Expanded Detail */}
                                                            {isExpanded && (
                                                                <div style={{ padding: '0 20px 20px', borderTop: '1px solid #f1f5f9' }}>
                                                                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px' }}>
                                                                        <thead>
                                                                            <tr>
                                                                                <th style={{ ...styles.th, background: '#f8fafc', borderRadius: '8px 0 0 0' }}>Ürün</th>
                                                                                <th style={{ ...styles.th, background: '#f8fafc' }}>Miktar</th>
                                                                                <th style={{ ...styles.th, background: '#f8fafc' }}>Birim Fiyat</th>
                                                                                <th style={{ ...styles.th, background: '#f8fafc' }}>Toplam</th>
                                                                                <th style={{ ...styles.th, background: '#f8fafc' }}>Neden</th>
                                                                                <th style={{ ...styles.th, background: '#f8fafc', borderRadius: '0 8px 0 0' }}>Açıklama</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {(returnItem.items || []).map((item, idx) => (
                                                                                <tr key={idx}>
                                                                                    <td style={{ ...styles.td, fontWeight: '500' }}>{item.productName}</td>
                                                                                    <td style={styles.td}>{item.quantity} adet</td>
                                                                                    <td style={styles.td}>{canViewPrices ? `${(item.unitPrice || 0).toFixed(2)} ₺` : '***'}</td>
                                                                                    <td style={{ ...styles.td, fontWeight: '600' }}>{canViewPrices ? `${(item.totalPrice || 0).toFixed(2)} ₺` : '***'}</td>
                                                                                    <td style={styles.td}>
                                                                                        <span style={getReasonBadge(item.reason)}>{reasonLabels[item.reason] || '-'}</span>
                                                                                    </td>
                                                                                    <td style={{ ...styles.td, color: '#64748b', fontStyle: 'italic' }}>{item.description || '-'}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>

                                                                    {returnItem.note && (
                                                                        <div style={{ marginTop: '12px', padding: '12px 16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#475569' }}>
                                                                            <strong>Not:</strong> {returnItem.note}
                                                                        </div>
                                                                    )}

                                                                    {!isCancelled && (
                                                                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                                                                            <button onClick={(e) => { e.stopPropagation(); handleCancelReturn(returnItem); }}
                                                                                style={{ padding: '8px 20px', borderRadius: '8px', border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
                                                                                onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                                                                                onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; }}>
                                                                                ✖ İadeyi İptal Et
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })()}

                    {activeTab === 'transactions' && (() => {
                        const totalCollections = transactions.filter(t => t.type === 'Tahsilat').reduce((sum, t) => sum + t.amount, 0);
                        const totalPayments = transactions.filter(t => t.type === 'Ödeme' || t.type === 'İade Ödemesi').reduce((sum, t) => sum + t.amount, 0);
                        const currencySymbol = customer?.currency === 'USD' ? '$' : '₺';

                        const getMethodIcon = (method) => {
                            const m = method?.toLowerCase() || '';
                            if (m.includes('kart')) return '💳';
                            if (m.includes('havale') || m.includes('eft')) return '🏦';
                            if (m.includes('çek')) return '🧾';
                            if (m.includes('senet')) return '📜';
                            return '💵'; // Nakit
                        };

                        return (
                            <div style={styles.tabContent}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                                    <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>Ödeme Geçmişi</h3>
                                </div>

                                {/* Summary Cards */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                                    <div style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', padding: '20px', borderRadius: '16px', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                                        <div style={{ fontSize: '32px', background: 'white', width: '56px', height: '56px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>📥</div>
                                        <div>
                                            <div style={{ color: '#065f46', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Alınan Ödemeler</div>
                                            <div style={{ color: '#059669', fontSize: '24px', fontWeight: 'bold' }}>{canViewPrices ? `${totalCollections.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${currencySymbol}` : '***'}</div>
                                        </div>
                                    </div>
                                    <div style={{ background: 'linear-gradient(135deg, #fef2f2, #fee2e2)', padding: '20px', borderRadius: '16px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                                        <div style={{ fontSize: '32px', background: 'white', width: '56px', height: '56px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>📤</div>
                                        <div>
                                            <div style={{ color: '#991b1b', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Yapılan İadeler</div>
                                            <div style={{ color: '#dc2626', fontSize: '24px', fontWeight: 'bold' }}>{canViewPrices ? `${totalPayments.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${currencySymbol}` : '***'}</div>
                                        </div>
                                    </div>
                                </div>

                                {transactions.length === 0 ? (
                                    <div style={{ ...styles.emptyState, background: '#f8fafc', borderRadius: '16px', border: '2px dashed #e2e8f0' }}>
                                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>💳</div>
                                        <div style={{ fontSize: '16px', color: '#64748b', fontWeight: '500' }}>Henüz ödeme kaydı bulunmuyor</div>
                                    </div>
                                ) : (
                                    <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                        <div style={{ ...styles.scrollableContainer, maxHeight: '600px' }} className="scrollable-container">
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                                                    <tr>
                                                        <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>İşlem Detayı</th>
                                                        <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>Vade</th>
                                                        <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>Ödeme Yöntemi</th>
                                                        <th style={{ padding: '16px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>Tutar</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {transactions.map(p => {
                                                        let isOverdue = false;
                                                        if (p.dueDate && p.status !== 'Ödendi') {
                                                            const due = new Date(p.dueDate);
                                                            const now = new Date();
                                                            now.setHours(0, 0, 0, 0);
                                                            if (due < now) isOverdue = true;
                                                        }
                                                        const isCollection = p.type === 'Tahsilat';
                                                        
                                                        return (
                                                            <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                                <td style={{ padding: '16px' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                        <div style={{ width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isCollection ? '#dcfce7' : '#fee2e2', color: isCollection ? '#166534' : '#991b1b', fontSize: '18px' }}>
                                                                            {isCollection ? '↓' : '↑'}
                                                                        </div>
                                                                        <div>
                                                                            <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{p.type || 'Ödeme'}</div>
                                                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                                                                                <span style={{ fontSize: '12px', color: '#64748b' }}>{new Date(p.date.seconds * 1000).toLocaleDateString('tr-TR')}</span>
                                                                                {p.description && (
                                                                                    <>
                                                                                        <span style={{ color: '#cbd5e1' }}>•</span>
                                                                                        <span style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.description}</span>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td style={{ padding: '16px' }}>
                                                                    {p.dueDate ? (
                                                                        <span style={{ padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', background: isOverdue ? '#fef2f2' : '#f1f5f9', color: isOverdue ? '#dc2626' : '#475569', border: `1px solid ${isOverdue ? '#fecaca' : '#e2e8f0'}`, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                                            {isOverdue && '⚠️'} {new Date(p.dueDate).toLocaleDateString('tr-TR')}
                                                                        </span>
                                                                    ) : '-'}
                                                                </td>
                                                                <td style={{ padding: '16px' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: 'fit-content' }}>
                                                                        <span style={{ fontSize: '16px' }}>{getMethodIcon(p.paymentMethod)}</span>
                                                                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#334155' }}>{p.paymentMethod || 'Nakit'}</span>
                                                                    </div>
                                                                </td>
                                                                <td style={{ padding: '16px', textAlign: 'right' }}>
                                                                    <div style={{ fontWeight: '700', fontSize: '16px', color: isCollection ? '#10b981' : '#ef4444' }}>
                                                                        {isCollection ? '+' : '-'}{canViewPrices ? `${p.amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${currencySymbol}` : '***'}
                                                                    </div>
                                                                    {p.currency && p.currency !== customer.currency && (
                                                                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                                                                            Orijinal: {canViewPrices ? `${p.amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${p.currency === 'USD' ? '$' : '₺'}` : '***'}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {activeTab === 'balance' && (() => {
                        const currencySymbol = customer?.currency === 'USD' ? '$' : '₺';
                        // Determine the type icon and color based on the entry type
                        const getEntryVisuals = (type) => {
                            const t = type?.toLowerCase() || '';
                            if (t.includes('satış') || t.includes('fatura') || t.includes('sipariş')) {
                                return { icon: '🛒', bg: '#eff6ff', color: '#3b82f6', border: '#bfdbfe', sign: '+' }; // Increases debt
                            }
                            if (t.includes('tahsilat') || t.includes('ödeme')) {
                                return { icon: '💰', bg: '#dcfce7', color: '#10b981', border: '#bbf7d0', sign: '-' }; // Decreases debt
                            }
                            if (t.includes('iade')) {
                                return { icon: '📦', bg: '#f5f3ff', color: '#8b5cf6', border: '#ddd6fe', sign: '-' }; // Decreases debt
                            }
                            if (t.includes('düzeltme')) {
                                return { icon: '✏️', bg: '#fef3c7', color: '#d97706', border: '#fde68a', sign: '' }; // Varies
                            }
                            return { icon: '📄', bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0', sign: '' };
                        };

                        const combinedHistory = [...balanceHistory];
                        transactions.forEach(tx => {
                            if (tx.status !== 'cancelled' && tx.type !== 'return_cancel') {
                                const exists = balanceHistory.some(bh => 
                                    Math.abs((bh.amount || 0) - (tx.amount || 0)) < 0.01 && 
                                    Math.abs((bh.date?.seconds || 0) - (tx.date?.seconds || 0)) < 10
                                );
                                if (!exists) {
                                    combinedHistory.push({
                                        id: tx.id,
                                        date: tx.date,
                                        type: tx.type || 'İşlem',
                                        description: tx.description || '-',
                                        amount: tx.amount,
                                        isTransaction: true,
                                        txType: tx.type
                                    });
                                }
                            }
                        });

                        combinedHistory.sort((a, b) => {
                            const dateA = a.date?.seconds ? a.date.seconds : (a.date ? new Date(a.date).getTime() / 1000 : 0);
                            const dateB = b.date?.seconds ? b.date.seconds : (b.date ? new Date(b.date).getTime() / 1000 : 0);
                            return dateA - dateB;
                        });

                        let runningBalance = 0;
                        const historyWithBalance = combinedHistory.map(entry => {
                            if (entry.isTransaction) {
                                if (entry.txType === 'Tahsilat') {
                                    runningBalance -= entry.amount;
                                } else {
                                    runningBalance += entry.amount;
                                }
                                return { ...entry, newBalance: runningBalance };
                            } else {
                                if (entry.newBalance !== undefined) {
                                    runningBalance = entry.newBalance;
                                } else {
                                    runningBalance += (entry.amount || 0);
                                }
                                return { ...entry, newBalance: runningBalance };
                            }
                        });

                        historyWithBalance.sort((a, b) => {
                            const dateA = a.date?.seconds ? a.date.seconds : (a.date ? new Date(a.date).getTime() / 1000 : 0);
                            const dateB = b.date?.seconds ? b.date.seconds : (b.date ? new Date(b.date).getTime() / 1000 : 0);
                            return dateB - dateA;
                        });

                        return (
                            <div style={styles.tabContent}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                                    <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>Bakiye Geçmişi Zaman Çizelgesi</h3>
                                    <div style={{ background: customer?.balance >= 0 ? '#ecfdf5' : '#fef2f2', padding: '8px 16px', borderRadius: '12px', border: `1px solid ${customer?.balance >= 0 ? '#10b981' : '#ef4444'}`, display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Güncel Durum:</span>
                                        <span style={{ fontSize: '16px', fontWeight: 'bold', color: customer?.balance >= 0 ? '#059669' : '#dc2626' }}>
                                            {canViewPrices ? `${Math.abs(customer?.balance || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${currencySymbol}` : '***'} {customer?.balance >= 0 ? 'Alacaklı' : 'Borçlu'}
                                        </span>
                                    </div>
                                </div>

                                {historyWithBalance.length === 0 ? (
                                    <div style={{ ...styles.emptyState, background: '#f8fafc', borderRadius: '16px', border: '2px dashed #e2e8f0' }}>
                                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📉</div>
                                        <div style={{ fontSize: '16px', color: '#64748b', fontWeight: '500' }}>Henüz hesap hareketi bulunmuyor</div>
                                    </div>
                                ) : (
                                    <div style={{ position: 'relative', paddingLeft: '24px', ...styles.scrollableContainer, maxHeight: '600px' }} className="scrollable-container">
                                        {/* Timeline Line */}
                                        <div style={{ position: 'absolute', left: '44px', top: '20px', bottom: '20px', width: '2px', background: '#e2e8f0', zIndex: 0 }}></div>
                                        
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative', zIndex: 1 }}>
                                            {historyWithBalance.map((entry, index) => {
                                                const visuals = getEntryVisuals(entry.type);
                                                // Calculate if the balance increased or decreased relative to previous state if possible, or just use absolute amount
                                                const amount = Math.abs(entry.amount || 0);
                                                
                                                // In accounting, positive balance usually means they owe us (borçlu), negative means we owe them (alacaklı)
                                                // We map newBalance color based on that
                                                const balColor = entry.newBalance > 0 ? '#059669' : (entry.newBalance < 0 ? '#dc2626' : '#64748b');
                                                const balBg = entry.newBalance > 0 ? '#ecfdf5' : (entry.newBalance < 0 ? '#fef2f2' : '#f8fafc');
                                                
                                                return (
                                                    <div key={entry.id || index} style={{ display: 'flex', gap: '20px', alignItems: 'stretch' }}>
                                                        {/* Timeline Icon */}
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '16px' }}>
                                                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: visuals.bg, border: `2px solid ${visuals.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', zIndex: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                                                {visuals.icon}
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Content Card */}
                                                        <div style={{ flex: 1, background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'box-shadow 0.2s' }} onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)'} onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.02)'}>
                                                            <div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                                                    <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>{entry.type || 'Bakiye Değişimi'}</span>
                                                                    <span style={{ fontSize: '12px', color: '#94a3b8', background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px' }}>
                                                                        {entry.date ? new Date(entry.date.seconds * 1000).toLocaleString('tr-TR', { dateStyle: 'long', timeStyle: 'short' }) : '-'}
                                                                    </span>
                                                                </div>
                                                                <div style={{ color: '#64748b', fontSize: '14px' }}>
                                                                    {entry.description || 'Açıklama bulunmuyor'}
                                                                </div>
                                                            </div>
                                                            
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                                                                <div style={{ textAlign: 'right' }}>
                                                                    <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Tutar</div>
                                                                    <div style={{ fontWeight: '700', fontSize: '16px', color: visuals.color }}>
                                                                        {visuals.sign} {canViewPrices ? `${amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${currencySymbol}` : '***'}
                                                                    </div>
                                                                </div>
                                                                <div style={{ width: '1px', height: '40px', background: '#e2e8f0' }}></div>
                                                                <div style={{ textAlign: 'right', background: balBg, padding: '10px 16px', borderRadius: '10px', border: `1px solid ${entry.newBalance > 0 ? '#a7f3d0' : (entry.newBalance < 0 ? '#fecaca' : '#e2e8f0')}`, minWidth: '140px' }}>
                                                                    <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px', textTransform: 'uppercase', fontWeight: '700' }}>Kalan Bakiye</div>
                                                                    <div style={{ fontWeight: 'bold', fontSize: '18px', color: balColor }}>
                                                                        {canViewPrices ? `${Math.abs(entry.newBalance || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${currencySymbol}` : '***'}
                                                                    </div>
                                                                    <div style={{ fontSize: '11px', color: balColor, fontWeight: '600', marginTop: '2px' }}>
                                                                        {entry.newBalance > 0 ? 'Borçlu' : (entry.newBalance < 0 ? 'Alacaklı' : 'Sıfırlandı')}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                </div>
            </div>

            {/* Return Modal */}
            {showReturnModal && (
                <ReturnModal
                    onClose={() => setShowReturnModal(false)}
                    onSave={handleCreateReturn}
                    customer={customer}
                    products={products}
                    orders={orders}
                />
            )}

            {/* Reconciliation Modal */}
            {showReconciliationModal && (
                <ReconciliationModal 
                    onClose={() => setShowReconciliationModal(false)}
                    onConfirm={handleGenerateReconciliation}
                    lastDate={customer.lastReconciliationDate}
                />
            )}

            {selectedInvoice && (
                <InvoiceDetailModal 
                    invoice={selectedInvoice} 
                    onClose={() => setSelectedInvoice(null)} 
                    canViewPrices={canViewPrices} 
                />
            )}
        </div>

    );
}

export default CustomerDetail;